import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  AgreementFlow,
  canSubmitAgreement,
  emptyAgreementState,
  shouldPollAgreement,
} from '../../../helpers/driverAgreement'
import { driverAgreements } from '../../../services/api/requests/driverAgreements/driverAgreements'
import { AgreementRecord } from '../../../types/driverAgreements'
import AgreementPdfPreview from './AgreementPdfPreview'

const VersionedDriverAgreement = ({
  knownRecord,
  onStatusChange,
  uid,
}: {
  knownRecord?: AgreementRecord
  onStatusChange: (record?: AgreementRecord) => void
  uid: string
}) => {
  const { t } = useTranslation()
  const [state, setState] = useState(emptyAgreementState)
  const [foreground, setForeground] = useState(
    document.visibilityState === 'visible' && document.hasFocus()
  )
  const [pollReads, setPollReads] = useState(0)
  const pollStarted = useRef(0)
  const entry = useRef(knownRecord)
  entry.current = knownRecord
  const statusChanged = useRef(onStatusChange)
  statusChanged.current = onStatusChange
  const flow = useMemo(
    () => new AgreementFlow(driverAgreements.forUser(uid), setState),
    [uid]
  )
  useEffect(() => {
    flow.open(entry.current)
    return () => flow.dispose()
  }, [flow, knownRecord?.id])
  useEffect(() => {
    const visibility = () => {
      const focused =
        document.visibilityState === 'visible' && document.hasFocus()
      setForeground(focused)
      if (!focused) flow.cancelRead()
    }
    document.addEventListener('visibilitychange', visibility)
    window.addEventListener('blur', visibility)
    window.addEventListener('focus', visibility)
    return () => {
      document.removeEventListener('visibilitychange', visibility)
      window.removeEventListener('blur', visibility)
      window.removeEventListener('focus', visibility)
    }
  }, [flow])
  const { record } = state
  const currentRecord = useRef(record)
  currentRecord.current = record
  useEffect(() => {
    setPollReads(0)
    pollStarted.current = Date.now()
  }, [flow, record?.id, record?.accepted_at])
  useEffect(() => {
    if (state.error === 'changed' || record?.accepted_at)
      statusChanged.current(
        currentRecord.current?.accepted_at ? currentRecord.current : undefined
      )
  }, [record?.accepted_at, state.error])
  useEffect(() => {
    if (
      !shouldPollAgreement(
        record,
        foreground,
        pollReads,
        Date.now() - pollStarted.current + 5000
      ) ||
      state.busy ||
      state.error
    )
      return undefined
    const timer = window.setTimeout(() => {
      if (
        !shouldPollAgreement(
          record,
          foreground,
          pollReads,
          Date.now() - pollStarted.current
        )
      )
        return
      setPollReads((count) => count + 1)
      flow.refresh()
    }, 5000)
    return () => window.clearTimeout(timer)
  }, [flow, foreground, pollReads, record, state.busy, state.error])
  const signed = Boolean(
    record?.accepted_at && record.render_status === 'ready'
  )
  return (
    <Stack spacing={2}>
      {state.error && (
        <Alert severity="error">{t(`agreement.${state.error}`)}</Alert>
      )}
      {state.busy && (
        <CircularProgress aria-label={t('agreement.loading')} size={24} />
      )}
      {record && (
        <>
          <Typography variant="h6">
            {t('agreement.version', { version: record.version.version })}
          </Typography>
          {record.accepted_at && (
            <Alert
              severity={
                record.render_status === 'failed' ? 'warning' : 'success'
              }
            >
              {t('agreement.accepted')}{' '}
              {record.render_status === 'pending' && t('agreement.pending')}
              {record.render_status === 'failed' && t('agreement.failed')}
            </Alert>
          )}
          <AgreementPdfPreview
            hash={signed ? undefined : record.version.template_sha256}
            key={`${uid}:${record.id}:${state.load}:${signed}`}
            onFailure={() => {
              flow.previewFailed()
            }}
            onReady={() => {
              flow.displayed(
                record.id,
                record.version.template_sha256,
                state.load
              )
            }}
            url={signed ? record.signed_copy_url : record.preview_url}
          />
          {!record.accepted_at && (
            <Box
              component="form"
              onSubmit={(event) => {
                event.preventDefault()
                flow.submit()
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.checked}
                    disabled={!state.displayed || state.busy}
                    onChange={(_, checked) => flow.check(checked)}
                  />
                }
                label={t('auth.register.agreement_checkbox_label')}
              />
              <Button
                disabled={!canSubmitAgreement(state)}
                type="submit"
                variant="contained"
              >
                {t('agreement.submit')}
              </Button>
            </Box>
          )}
          {record.accepted_at && (
            <Button
              disabled={state.busy}
              onClick={() => {
                setPollReads(0)
                pollStarted.current = Date.now()
                flow.refresh()
              }}
            >
              {t('agreement.refresh')}
            </Button>
          )}
        </>
      )}
      {state.error && (
        <Button
          disabled={state.busy}
          onClick={() => {
            flow.retry()
          }}
        >
          {t('agreement.retry')}
        </Button>
      )}
    </Stack>
  )
}

export default VersionedDriverAgreement
